///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shared titlebar visibility toggle for dataset browser windows. Every
/// dataset browser previously duplicated this button; it derives entirely
/// from the datasetWindowMeta registry now.
///

import { cDatasetWindowMeta } from "@/components/windows/datasetWindowMeta"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { useCallback } from "react"

export function DatasetVisibilityToggle({ windowType }: { windowType: string }) {
	const meta = cDatasetWindowMeta[windowType]
	const datasetType = meta?.datasetType
	const dispatch = useAppDispatch()
	const visible = useAppSelector(state =>
		datasetType !== undefined && state.earthbrowser.visibleDatasets.includes(datasetType)
	)
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	const toggle = useCallback(() => {
		if (!datasetType) return
		dispatch(sendDatasetAction({ type: datasetType, action: visible ? "disable" : "enable" }))
		if (meta.layerName && layerManager) {
			layerManager.setLayerVisible(meta.layerName, !visible)
		}
	}, [dispatch, datasetType, visible, meta, layerManager])

	if (!meta || !datasetType) return null

	const plural = meta.plural ?? "layer"
	return (
		<button
			onClick={toggle}
			className={cn(
				"flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center",
				"transition-all duration-300",
				"focus-visible:outline-none focus-visible:ring-2",
				meta.toggleRingClass ?? "focus-visible:ring-cyan-500/50",
				visible
					? meta.toggleOnClass ?? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
					: "bg-white/5 text-white/40 hover:bg-white/10"
			)}
			aria-label={visible ? "Hide from map" : "Show on map"}
			title={visible ? `Hide ${plural} from map` : `Show ${plural} on map`}
		>
			<div className={cn(
				"w-2.5 h-2.5 rounded-full bg-current transition-transform duration-300",
				visible && "scale-110"
			)} />
		</button>
	)
}
